// src/pages/portal/Cart.tsx
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { useCart } from "@/lib/portal/cartStore";
import { usePortal } from "@/lib/portal/PortalContext";
import { summarizeOrder } from "@/lib/pricing";
import { formatPrice } from "@/lib/formatPrice";
import { authHeaders } from "@/lib/api";

export function PortalCart() {
  const { t } = useTranslation();
  const cart = useCart();
  const navigate = useNavigate();
  const { customer } = usePortal();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!customer) return null;
  const tier = customer.pricingTier;

  const enriched = cart.items.map((it) => ({ ...it }));

  const summary = summarizeOrder(
    enriched.map((it) => ({ listPrice: it.listPriceEUR, tier, qty: it.qty })),
  );

  async function submitOrder() {
    // M2-03: send ONLY product + quantity; the server validates every
    // product and recomputes all prices/totals with Decimal.
    setSubmitting(true);
    setError("");
    try {
      const r = await fetch("/api/portal/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          lines: cart.items.map((it) => ({
            product_id: Number(it.sku),
            quantity: it.qty,
          })),
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail ?? `HTTP ${r.status}`);
      }
      cart.clear();
      navigate("/portal/orders");
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-xl font-510 tracking-tight text-fg-primary">{t("portal.cart.title")}</h2>
        {cart.itemCount > 0 && (
          <span className="text-xs text-fg-tertiary">
            {cart.itemCount} {cart.itemCount === 1 ? t("portal.cart.item") : t("portal.cart.items")}
          </span>
        )}
      </header>

      {cart.items.length === 0 ? (
        <EmptyState
          title={t("portal.cart.empty")}
          description={t("portal.cart.emptyHint")} 
          icon={<ShoppingBag className="h-5 w-5 text-fg-quaternary" strokeWidth={1.5} />}
          action={
            <Link to="/portal/catalog" className="inline-flex items-center gap-2 rounded-md bg-benchmark-teal px-3 py-1.5 text-sm font-510 text-bg-canvas hover:bg-benchmark-teal-bright">
              {t("portal.cart.browseCatalog")}
            </Link>
          }
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-2">
            {enriched.map((it, i) => {
              const price = summary.lines[i]?.price;
              if (!price) return null;
              // An order request is allowed, but it does not verify availability.
              return (
                <Card key={it.sku}>
                  <CardContent className="flex flex-wrap items-center gap-3 p-4">
                    <div className="h-16 w-20 flex-shrink-0 overflow-hidden rounded-md border border-border bg-bg-elevated">
                      {it.image && it.image !== "x" ? (
                        <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-fg-quaternary">
                          <ShoppingBag className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-[10px]">
                        <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-fg-secondary">{it.sku}</code>
                        <Badge variant="default" size="sm">
                          {t("product.availabilityUnknown", "Availability not verified")}
                        </Badge>
                        <span className="ml-auto text-[10px] text-fg-quaternary">{it.brand}</span>
                      </div>
                      <div className="mt-1 truncate text-sm font-510 text-fg-primary">{it.name}</div>
                      {price.discount > 0 && (
                        <Badge variant="teal" size="sm">
                          {price.discountLabel} {t("portal.cart.discountNote")} {price.discountLabel}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => cart.setQty(it.sku, it.qty - 1)}
                        aria-label={t("portal.cart.decreaseQuantity")}
                        className="rounded border border-border bg-bg-elevated p-1 text-fg-tertiary hover:bg-bg-hover hover:text-fg-primary disabled:opacity-30"
                      >
                        <Minus className="h-3 w-3" strokeWidth={1.5} />
                      </button>
                      <span className="w-7 text-center font-mono text-sm">{it.qty}</span>
                      <button
                        type="button"
                        onClick={() => cart.setQty(it.sku, it.qty + 1)}
                        aria-label={t("portal.cart.increaseQuantity")}
                        className="rounded border border-border bg-bg-elevated p-1 text-fg-tertiary hover:bg-bg-hover hover:text-fg-primary"
                      >
                        <Plus className="h-3 w-3" strokeWidth={1.5} />
                      </button>
                      <button
                        type="button"
                        onClick={() => cart.removeItem(it.sku)}
                        aria-label={t("portal.cart.remove")}
                        className="rounded p-1 text-accent-rose hover:bg-accent-rose/10"
                      >
                        <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-510 text-fg-primary">
                        {price.isQuoteOnly ? "—" : formatPrice(price.lineTotal, "EUR")}
                      </div>
                      {!price.isQuoteOnly && price.discount === 0 && (
                        <div className="text-[10px] text-fg-quaternary">@ {formatPrice(price.unitPrice, "EUR")}</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardContent className="space-y-4 p-5">
              <h3 className="text-sm font-510 text-fg-secondary">{t("portal.cart.orderSummary")}</h3>
              <div className="flex justify-between text-sm">
                <span className="text-fg-tertiary">{t("portal.cart.subtotal")}</span>
                <span className="font-mono font-510 text-fg-primary">
                  {formatPrice(summary.subtotal, "EUR")}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-fg-tertiary">{t("portal.cart.taxEstimate")}</span>
                <span className="text-fg-quaternary">{t("portal.cart.calculatedAtFulfilment")}</span>
              </div>
              <div className="border-t border-border pt-3">
                <div className="flex justify-between text-base">
                  <span className="font-510 text-fg-secondary">{t("portal.cart.total")}</span>
                  <span className="font-mono text-lg font-510 text-fg-primary">
                    {formatPrice(summary.subtotal, "EUR")}
                  </span>
                </div>
              </div>
              {error && (
                <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-2 text-xs text-accent-rose">
                  {error}
                </div>
              )}
              <Button
                className="w-full"
                disabled={cart.items.length === 0 || submitting}
                onClick={submitOrder}
              >
                {submitting ? t("portal.cart.submitting") : t("portal.cart.submitOrderRequest")}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => cart.clear()}>
                {t("portal.cart.emptyCart")}
              </Button>
              <p className="text-[10px] text-fg-quaternary">
                {t("portal.cart.submissionNote")}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
